import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MapPin,
  Calendar,
  Users,
  Wallet,
  Car,
  BedDouble,
  Clock,
  ArrowLeft,
  Share2,
  Download,
  CheckCircle2,
  Sparkles,
  AlertCircle,
  AlertTriangle,
  Compass,
  FileText,
  ShieldCheck,
  ShieldAlert,
  PhoneCall,
  Loader2,
  ShoppingBag,
  HeartPulse,
  Award,
  GitPullRequest,
  UserCheck,
  XCircle,
  Send,
  Check,
  Plus,
  Edit3
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import tripsApi from '../api/tripsApi';
import Loader from './Loader';
import { useAuth } from '../context/AuthContext';
import SocialShareModal from './SocialShareModal';

export default function ViewTrip() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Social Share & Collaboration States
  const [showShareModal, setShowShareModal] = useState(false);
  const [showProposeModal, setShowProposeModal] = useState(false);
  const [isSubmittingProposal, setIsSubmittingProposal] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [statusFeedback, setStatusFeedback] = useState('');

  // Companion PR Draft
  const [proposeDraft, setProposeDraft] = useState({
    note: '',
    budget: '',
    startDate: '',
    endDate: '',
    destination: '',
    travelerRequirements: '',
  });

  useEffect(() => {
    let isMounted = true;
    const fetchTrip = async () => {
      setLoading(true);
      setError(null);
      try {
        let data = null;
        try {
          data = await tripsApi.getSharedTrip(id);
        } catch (e) {
          data = await tripsApi.getTripById(id);
        }

        if (isMounted && data) {
          setTrip(data);
          setProposeDraft({
            note: '',
            budget: data.budget || '',
            startDate: data.startDate || '',
            endDate: data.endDate || '',
            destination: data.destination || '',
            travelerRequirements: '',
          });
        }
      } catch (err) {
        console.error('Failed to fetch trip:', err);
        if (isMounted) {
          setError('Failed to load trip itinerary. The trip may not exist or has been deleted.');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchTrip();
    return () => {
      isMounted = false;
    };
  }, [id]);

  // Handle Companion Accept & Join Trip
  const handleAcceptTrip = async () => {
    try {
      setIsAccepting(true);
      await tripsApi.acceptTrip(trip.id);
      setStatusFeedback('You have successfully accepted and joined this trip! Your traveler slot is reserved.');
      const updated = await tripsApi.getSharedTrip(trip.id);
      setTrip(updated);
    } catch (err) {
      console.error('Accept trip error:', err);
      alert(`Failed to accept trip: ${err.message || 'Unknown error'}`);
    } finally {
      setIsAccepting(false);
    }
  };

  // Handle Companion Propose Changes (PR)
  const handleSubmitProposal = async (e) => {
    e.preventDefault();
    if (!proposeDraft.note.trim()) {
      alert('Please describe your proposed changes or reason for the organizer.');
      return;
    }

    try {
      setIsSubmittingProposal(true);
      const proposedPlan = {
        title: trip.title,
        destination: proposeDraft.destination || trip.destination,
        startDate: proposeDraft.startDate || trip.startDate,
        endDate: proposeDraft.endDate || trip.endDate,
        budget: proposeDraft.budget ? Number(proposeDraft.budget) : trip.budget,
        travelerRequirements: proposeDraft.travelerRequirements,
      };

      await tripsApi.createChangeRequest(trip.id, {
        title: proposeDraft.title?.trim() || `Modifications to ${trip.title || 'trip'}`,
        notes: proposeDraft.note?.trim() || '',
        proposedChanges: proposedPlan,
      });

      setShowProposeModal(false);
      setStatusFeedback('Your change proposal (PR) has been submitted to the organizer for review!');
      const updated = await tripsApi.getSharedTrip(trip.id);
      setTrip(updated);
    } catch (err) {
      console.error('Submit proposal error:', err);
      alert(`Failed to submit change proposal: ${err.message || 'Unknown error'}`);
    } finally {
      setIsSubmittingProposal(false);
    }
  };

  // PDF Export
  const handleExportPDF = () => {
    if (!trip || exportingPdf) return;
    setExportingPdf(true);

    setTimeout(() => {
      try {
        const doc = new jsPDF('p', 'mm', 'a4');
        const margin = 14;
        let y = 20;

        doc.setFontSize(22);
        doc.setTextColor(42, 157, 143);
        doc.setFont('helvetica', 'bold');
        doc.text(trip.title || trip.destination || 'Trip Itinerary', margin, y);
        y += 8;

        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.setFont('helvetica', 'normal');
        doc.text(
          `${trip.tripType || trip.travelStyle || 'Travel Plan'} | ${trip.totalDays || 3} Days | ${trip.travelers || 2} Travelers | Budget: Rs. ${Number(trip.budget || 0).toLocaleString()}`,
          margin,
          y
        );
        y += 12;

        const itinerary = trip.days || [];
        if (itinerary.length > 0) {
          const rows = itinerary.map((d, i) => [
            `Day ${d.dayNumber || i + 1}`,
            d.title || `Day ${i + 1}`,
            d.route || 'Sightseeing',
            d.hotelName || 'Curated Stay',
            `Rs. ${(Number(d.transportCost || 0) + Number(d.hotelCost || 0)).toLocaleString()}`,
          ]);

          autoTable(doc, {
            startY: y,
            head: [['Day', 'Title', 'Route', 'Stay', 'Est. Cost']],
            body: rows,
            margin: { left: margin, right: margin },
            headStyles: { fillColor: [42, 157, 143], textColor: [255, 255, 255], fontStyle: 'bold' },
            bodyStyles: { fontSize: 8 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
          });
        }

        doc.save(`${(trip.destination || 'Trip').replace(/\s+/g, '_')}_Itinerary.pdf`);
      } catch (e) {
        console.error('PDF export failed:', e);
      } finally {
        setExportingPdf(false);
      }
    }, 400);
  };

  if (loading) {
    return <Loader text="Loading Journey Details..." />;
  }

  if (error || !trip) {
    return (
      <div className="p-8 max-w-xl mx-auto text-center space-y-4">
        <AlertCircle className="w-12 h-12 text-rose-500 mx-auto" />
        <h2 className="text-xl font-bold text-gray-900">Trip Not Found</h2>
        <p className="text-sm text-gray-500">{error || 'This trip does not exist or has been removed.'}</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="px-6 py-2.5 bg-[#2A9D8F] text-white font-bold rounded-xl shadow-md hover:bg-[#238177] cursor-pointer"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  const itinerary = trip.days || [];

  // Robust financial extraction
  const breakdown = trip.costBreakdownFull || {};
  let transport = Number(breakdown.transportINR || (Number(breakdown.interCityTransportINR || 0) + Number(breakdown.intraCityTransportINR || 0))) || 0;
  let stay = Number(breakdown.stayINR) || 0;
  let food = Number(breakdown.foodINR) || 0;
  let activities = Number(breakdown.activitiesINR) || 0;
  let misc = Number(breakdown.permitsMiscINR || breakdown.hiddenCostsINR) || 0;

  // Fallback calculation from individual days
  if (!transport && !stay && !activities) {
    itinerary.forEach((d) => {
      transport += Number(d.transportCost || 0);
      stay += Number(d.hotelCost || 0);
      food += 1200;
      const acts = d.dailyActivities || d.activities || [];
      acts.forEach((a) => {
        activities += Number(a.estimatedINR || a.costINR || a.cost || 0);
      });
    });
  }

  const totalCalculated = transport + stay + food + activities + misc;

  const pieData = [
    { name: 'Transport', value: transport || 8000, color: '#2A9D8F' },
    { name: 'Stay', value: stay || 14000, color: '#E76F51' },
    { name: 'Food', value: food || 7000, color: '#F4A261' },
    { name: 'Activities', value: activities || 5000, color: '#3B82F6' },
    ...(misc > 0 ? [{ name: 'Permits & Misc', value: misc, color: '#8B5CF6' }] : []),
  ].filter((item) => item.value > 0);

  const costSummary = trip.totalCostSummary || {};
  const travelInsights = trip.travelInsights || {};
  const logistics = trip.logistics || {};
  const survivalGuide = trip.survivalGuide || {};

  // Collaboration checks
  const isOwner = trip.userId === user?.id || (trip.collaborators || []).some((c) => c.userId === user?.id && c.role === 'OWNER');
  const myCollab = (trip.collaborators || []).find((c) => c.userId === user?.id);
  const isAccepted = myCollab && myCollab.status === 'ACCEPTED';
  const pendingRequestsCount = (trip.changeRequests || []).filter((r) => r.status === 'PENDING').length;
  const collaborators = trip.collaborators || [];
  const maxSlots = trip.travelers || 4;

  const organizerCollab = collaborators.find((c) => c.slotNumber === 1 || c.role === 'OWNER');
  const organizerUser = organizerCollab?.user || {
    fullName: user?.fullName || 'Trip Organizer',
    email: user?.email || '',
    avatarUrl: user?.avatarUrl,
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto space-y-8 bg-slate-50 min-h-screen">
      {/* Success Notification Alert */}
      {statusFeedback && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center justify-between gap-3 text-emerald-800 text-xs sm:text-sm font-semibold animate-fade-in">
          <div className="flex items-center gap-2.5">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            <span>{statusFeedback}</span>
          </div>
          <button
            onClick={() => setStatusFeedback('')}
            className="text-emerald-700 hover:text-emerald-900 text-xs font-bold cursor-pointer"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* ── Top Bar: Back & Actions ── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-xl border border-gray-200 text-xs font-bold transition-all shadow-xs cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Dashboard</span>
        </button>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Organizer PR Review Hub & Actions */}
          {isOwner && (
            <>
              <button
                onClick={() => navigate(`/dashboard/create-trip?sharedTripId=${trip.id}`)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-xl border border-gray-200 text-xs font-bold transition-all shadow-xs cursor-pointer"
              >
                <Edit3 className="w-4 h-4 text-[#2A9D8F]" />
                <span>Edit Trip</span>
              </button>

              <button
                onClick={() => navigate(`/dashboard/trip/${trip.id}/change-requests`)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-50 hover:bg-amber-100 text-amber-900 rounded-xl border border-amber-300 text-xs font-bold transition-all shadow-xs cursor-pointer"
              >
                <GitPullRequest className="w-4 h-4 text-amber-600" />
                <span>Change Requests</span>
                {pendingRequestsCount > 0 && (
                  <span className="w-5 h-5 rounded-full bg-amber-600 text-white text-[10px] flex items-center justify-center font-black">
                    {pendingRequestsCount}
                  </span>
                )}
              </button>

              {/* Social Share Button (Instagram Post Preview) - OWNER ONLY */}
              <button
                onClick={() => setShowShareModal(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-purple-600 via-pink-600 to-amber-500 hover:opacity-95 text-white rounded-xl text-xs font-bold transition-all shadow-sm cursor-pointer"
              >
                <Share2 className="w-4 h-4" />
                <span>Share Trip</span>
              </button>
            </>
          )}

          {/* Companion Action Buttons */}
          {!isOwner && (
            <>
              {!isAccepted ? (
                <button
                  onClick={handleAcceptTrip}
                  disabled={isAccepting}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black transition-all shadow-sm cursor-pointer disabled:opacity-50"
                >
                  {isAccepting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <UserCheck className="w-4 h-4" />
                  )}
                  <span>Accept &amp; Join (Fill Slot)</span>
                </button>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-xl text-xs font-bold">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Slot #{myCollab?.slotNumber || 2} Reserved</span>
                </span>
              )}

              <button
                onClick={() => navigate(`/dashboard/create-trip?forkTrip=${trip.id}`)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-teal-600 to-cyan-700 hover:from-teal-700 hover:to-cyan-800 text-white rounded-xl text-xs font-bold transition-all shadow-sm cursor-pointer"
              >
                <GitPullRequest className="w-4 h-4" />
                <span>Propose Changes (PR)</span>
              </button>
            </>
          )}

          {/* PDF Export */}
          <button
            onClick={handleExportPDF}
            disabled={exportingPdf}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-xl border border-gray-200 text-xs font-bold transition-all shadow-xs cursor-pointer"
          >
            {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin text-[#2A9D8F]" /> : <Download className="w-4 h-4 text-[#2A9D8F]" />}
            <span>Export PDF</span>
          </button>
        </div>
      </div>

      {/* ── Traveler Slots Tracker Strip ── */}
      <div className="bg-white rounded-2xl p-4 border border-gray-200 shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#2A9D8F]" />
            <span className="text-xs font-bold text-gray-900 uppercase tracking-wider">
              Traveler Roster ({collaborators.length} / {maxSlots} Slots Filled)
            </span>
          </div>
          <span className="text-[11px] text-gray-400">
            Invite companions via deep link to occupy slots
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {Array.from({ length: Math.max(maxSlots, collaborators.length) }).map((_, idx) => {
            const slotNum = idx + 1;
            const collab = collaborators.find((c) => c.slotNumber === slotNum);
            const isSlot1 = slotNum === 1;

            if (collab) {
              const name = collab.travelerInfo?.name || collab.user?.fullName || (isSlot1 ? 'Organizer' : `Traveler ${slotNum}`);
              return (
                <div
                  key={slotNum}
                  className={`p-2.5 rounded-xl border flex items-center gap-2.5 ${
                    isSlot1
                      ? 'bg-teal-50/60 border-teal-200'
                      : collab.status === 'ACCEPTED'
                      ? 'bg-emerald-50/50 border-emerald-200'
                      : 'bg-amber-50/50 border-amber-200'
                  }`}
                >
                  <img
                    src={collab.user?.avatarUrl || `https://api.dicebear.com/7.x/bottts/svg?seed=slot-${slotNum}`}
                    alt={name}
                    className="w-7 h-7 rounded-lg object-cover shrink-0"
                  />
                  <div className="overflow-hidden">
                    <span className="text-[11px] font-bold text-gray-900 block truncate">{name}</span>
                    <span className="text-[10px] text-gray-500 block truncate">
                      {isSlot1 ? 'Organizer' : collab.status === 'ACCEPTED' ? 'Joined' : 'Pending PR'}
                    </span>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={slotNum}
                className="p-2.5 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 flex items-center justify-center gap-1.5 text-gray-400"
              >
                <Plus className="w-3.5 h-3.5" />
                <span className="text-[11px] font-semibold">Slot #{slotNum}: Open</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Hero Banner ── */}
      <div
        className="relative bg-slate-900 rounded-[2.5rem] p-8 md:p-12 shadow-xl overflow-hidden flex flex-col justify-end min-h-[400px] text-white"
        style={{
          backgroundImage: `url(${trip.coverImageUrl || 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1600'})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-transparent pointer-events-none"></div>

        <div className="relative z-10 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/20 backdrop-blur-md border border-teal-400/30 text-teal-300 text-xs font-bold uppercase tracking-wider">
            <Compass className="w-3.5 h-3.5" />
            <span>{trip.travelStyle || trip.tripType || 'Curated Expedition'}</span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white max-w-3xl">
            {trip.title || trip.destination}
          </h1>

          <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-slate-200 pt-2">
            <span className="flex items-center gap-1.5 font-semibold">
              <MapPin className="w-4 h-4 text-[#2A9D8F]" />
              <span>{trip.destination}</span>
            </span>

            <span className="flex items-center gap-1.5 font-semibold">
              <Calendar className="w-4 h-4 text-[#2A9D8F]" />
              <span>{trip.totalDays || itinerary.length} Days</span>
            </span>

            <span className="flex items-center gap-1.5 font-semibold">
              <Users className="w-4 h-4 text-[#2A9D8F]" />
              <span>{trip.travelers || 2} Traveler(s)</span>
            </span>

            <span className="flex items-center gap-1.5 font-extrabold text-teal-400">
              <Wallet className="w-4 h-4" />
              <span>Total Budget: ₹{parseFloat(trip.budget || totalCalculated || 50000).toLocaleString()}</span>
            </span>

            {trip.startDate && (
              <span className="text-xs text-teal-300 bg-teal-950/70 px-3 py-1 rounded-xl border border-teal-500/30 font-medium">
                {trip.startDate} {trip.endDate ? `to ${trip.endDate}` : ''}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Day-by-Day Journey ── */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-black text-gray-900 tracking-tight">Day-by-Day Itinerary</h2>
          <span className="text-xs font-semibold text-gray-500">{itinerary.length} Days Planned</span>
        </div>

        <div className="space-y-6">
          {itinerary.map((day, idx) => {
            const dailyActs = day.dailyActivities || day.activities || [];
            const weather = typeof day.predictedWeather === 'object'
              ? `${day.predictedWeather?.conditions || ''} ${day.predictedWeather?.temperatureHigh ? `(${day.predictedWeather.temperatureHigh})` : ''}`
              : (day.weatherConditions || '');

            return (
              <div
                key={idx}
                className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm space-y-6"
              >
                {/* Day Header */}
                <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-4">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-2xl bg-[#2A9D8F] text-white flex items-center justify-center font-bold text-sm shadow-sm">
                      {day.dayNumber || day.day || idx + 1}
                    </span>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{day.title || `Day ${idx + 1}`}</h3>
                      <div className="flex flex-wrap items-center gap-2 mt-0.5">
                        {day.route && (
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5 text-gray-400" />
                            <span>{day.route}</span>
                          </p>
                        )}
                        {day.distance && (
                          <span className="text-xs text-gray-400 font-mono">({day.distance})</span>
                        )}
                        {day.travelTime && (
                          <span className="text-xs text-gray-400 font-mono">· ⏱ {day.travelTime}</span>
                        )}
                        {day.dailyPacing && (
                          <span className="px-2 py-0.5 rounded-md bg-teal-50 text-teal-700 text-[10px] font-bold uppercase">
                            {day.dailyPacing}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {weather && (
                    <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-3 py-1.5 rounded-xl border border-amber-200 flex items-center gap-1.5">
                      <span>☀️</span>
                      <span>{weather}</span>
                    </span>
                  )}
                </div>

                {/* Transit & Stay Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Transit Card */}
                  <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                        <Car className="w-4 h-4 text-[#2A9D8F]" />
                        <span>Transit &amp; Route</span>
                      </span>
                      {Number(day.transportCost || day.transportDetails?.estimatedINR) > 0 && (
                        <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200 font-mono">
                          ₹{Number(day.transportCost || day.transportDetails?.estimatedINR).toLocaleString()}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-gray-900">
                      {day.transportMode || day.transportDetails?.mode || 'Local Transfer'}
                    </p>
                    {(day.transportBookingPlatform || day.transportDetails?.bookingPlatform) && (
                      <span className="text-[11px] text-gray-400 block">
                        Platform: {day.transportBookingPlatform || day.transportDetails?.bookingPlatform}
                      </span>
                    )}
                  </div>

                  {/* Stay Card */}
                  <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                        <BedDouble className="w-4 h-4 text-amber-500" />
                        <span>Accommodation</span>
                      </span>
                      {Number(day.hotelCost || day.accommodation?.estimatedINR) > 0 && (
                        <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200 font-mono">
                          ₹{Number(day.hotelCost || day.accommodation?.estimatedINR).toLocaleString()}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-gray-900">
                      {day.hotelName || day.accommodation?.hotelName || 'Curated Eco Stay'}
                    </p>
                    {(day.hotelRecommendationReason || day.accommodation?.whyRecommended) && (
                      <p className="text-xs text-gray-500 leading-relaxed">
                        {day.hotelRecommendationReason || day.accommodation?.whyRecommended}
                      </p>
                    )}
                  </div>
                </div>

                {/* Experience Description */}
                {day.experienceDescription && (
                  <div className="p-4 rounded-2xl bg-teal-50/50 border border-teal-100/80 text-xs text-teal-900 leading-relaxed">
                    <strong className="font-bold block mb-1">Curated Daily Experience:</strong>
                    {day.experienceDescription}
                  </div>
                )}

                {/* Planned Activities */}
                {dailyActs.length > 0 && (
                  <div className="space-y-4 pt-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-black text-teal-800 uppercase tracking-wider flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-amber-400" />
                        <span>Planned Activities ({dailyActs.length})</span>
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {dailyActs.map((act, actIdx) => {
                        const actName = typeof act === 'object' ? (act.name || act.title || act.activityName || 'Activity') : act;
                        const actDesc = typeof act === 'string' ? '' : (act.description || act.details || '');
                        const actCost = typeof act === 'object' ? (act.estimatedINR || act.costINR || act.cost || 0) : 0;
                        const actTime = typeof act === 'object' ? act.time : '';
                        const actImg = typeof act === 'object' && act.imageUrl ? act.imageUrl : null;

                        return (
                          <div
                            key={actIdx}
                            className="group relative bg-white rounded-2xl border border-gray-200 hover:border-teal-400 overflow-hidden transition-all shadow-xs hover:shadow-md flex flex-col"
                          >
                            {actImg && (
                              <div className="relative h-44 w-full overflow-hidden bg-slate-100">
                                <img
                                  src={actImg}
                                  alt={actName}
                                  onError={(e) => {
                                    e.target.src = `https://picsum.photos/seed/${encodeURIComponent(actName)}/600/400`;
                                  }}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                                {actCost > 0 && (
                                  <div className="absolute top-3 right-3 px-2.5 py-1 bg-black/70 backdrop-blur-md border border-emerald-500/40 rounded-lg text-emerald-400 text-xs font-bold font-mono">
                                    ₹{Number(actCost).toLocaleString()}
                                  </div>
                                )}
                                {actTime && (
                                  <div className="absolute bottom-3 left-3 px-2.5 py-1 bg-teal-900/80 backdrop-blur-md border border-teal-400/40 rounded-lg text-teal-200 text-[11px] font-bold flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    <span>{actTime}</span>
                                  </div>
                                )}
                              </div>
                            )}

                            <div className="p-4 flex-1 flex flex-col justify-between space-y-2">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-start gap-2.5">
                                  <CheckCircle2 className="w-4 h-4 text-[#2A9D8F] shrink-0 mt-0.5" />
                                  <div>
                                    <h5 className="text-sm font-bold text-gray-900 leading-snug">{actName}</h5>
                                    {act.location && (
                                      <p className="text-[11px] text-gray-400 flex items-center gap-1 mt-0.5">
                                        <MapPin className="w-3 h-3 text-gray-400" />
                                        <span>{act.location}</span>
                                      </p>
                                    )}
                                  </div>
                                </div>
                                {!actImg && actCost > 0 && (
                                  <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200 font-mono shrink-0">
                                    ₹{Number(actCost).toLocaleString()}
                                  </span>
                                )}
                              </div>

                              {actDesc && (
                                <p className="text-xs text-gray-600 leading-relaxed pl-6">
                                  {actDesc}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Financial & Budget Allocation Breakdown ── */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm space-y-6">
        <div className="border-b border-gray-100 pb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
              <Wallet className="w-5 h-5 text-[#2A9D8F]" />
              <span>Financial &amp; Budget Allocation</span>
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Comprehensive expense distribution across categories</p>
          </div>
          {costSummary.safeCostINR && (
            <div className="text-right">
              <span className="text-[11px] uppercase tracking-wider text-gray-400 font-bold block">Safe Target Budget</span>
              <span className="text-lg font-black text-emerald-600">₹{costSummary.safeCostINR.toLocaleString()}</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Donut Chart with fixed safe dimensions */}
          <div className="lg:col-span-5 flex flex-col items-center justify-center">
            <div className="h-64 w-full min-w-0 relative flex items-center justify-center">
              <ResponsiveContainer width="100%" height={260} minWidth={0}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={65}
                    outerRadius={95}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(val) => `₹${Number(val).toLocaleString()}`}
                    contentStyle={{ backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', color: '#1e293b' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">Total Est.</span>
                <span className="text-xl font-black text-gray-900 mt-0.5">₹{totalCalculated.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Detailed Financial Category Table */}
          <div className="lg:col-span-7 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Inter-City Transport */}
              <div className="p-3.5 bg-gray-50 rounded-2xl border border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-3 h-3 rounded-full bg-[#2A9D8F]"></div>
                  <span className="text-xs font-bold text-gray-700">Transit &amp; Travel</span>
                </div>
                <span className="text-sm font-black text-gray-900 font-mono">₹{transport.toLocaleString()}</span>
              </div>

              {/* Stay / Accommodation */}
              <div className="p-3.5 bg-gray-50 rounded-2xl border border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-3 h-3 rounded-full bg-[#E76F51]"></div>
                  <span className="text-xs font-bold text-gray-700">Stay &amp; Lodging</span>
                </div>
                <span className="text-sm font-black text-gray-900 font-mono">₹{stay.toLocaleString()}</span>
              </div>

              {/* Food & Dining */}
              <div className="p-3.5 bg-gray-50 rounded-2xl border border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-3 h-3 rounded-full bg-[#F4A261]"></div>
                  <span className="text-xs font-bold text-gray-700">Food &amp; Dining</span>
                </div>
                <span className="text-sm font-black text-gray-900 font-mono">₹{food.toLocaleString()}</span>
              </div>

              {/* Activities & Tours */}
              <div className="p-3.5 bg-gray-50 rounded-2xl border border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-3 h-3 rounded-full bg-[#3B82F6]"></div>
                  <span className="text-xs font-bold text-gray-700">Activities &amp; Sightseeing</span>
                </div>
                <span className="text-sm font-black text-gray-900 font-mono">₹{activities.toLocaleString()}</span>
              </div>

              {/* Misc / Permits */}
              {misc > 0 && (
                <div className="sm:col-span-2 p-3.5 bg-gray-50 rounded-2xl border border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-3 h-3 rounded-full bg-[#8B5CF6]"></div>
                    <span className="text-xs font-bold text-gray-700">Permits, Entry Fees &amp; Misc</span>
                  </div>
                  <span className="text-sm font-black text-gray-900 font-mono">₹{misc.toLocaleString()}</span>
                </div>
              )}
            </div>

            {/* Granular Food & Dining Split */}
            {trip.costBreakdownFull?.foodBreakdownINR && typeof trip.costBreakdownFull.foodBreakdownINR === 'object' && (
              <div className="p-4 rounded-2xl bg-amber-50/40 border border-amber-100 space-y-2">
                <span className="text-[11px] uppercase tracking-wider font-bold text-amber-900 block">
                  Detailed Daily Meal Allocation
                </span>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="p-2 rounded-xl bg-white border border-amber-200/60 text-center">
                    <span className="text-[10px] text-gray-500 block font-semibold">Breakfast</span>
                    <span className="text-xs font-bold text-gray-900 font-mono">₹{Number(trip.costBreakdownFull.foodBreakdownINR.breakfast || 0).toLocaleString()}</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-amber-200/60 text-center">
                    <span className="text-[10px] text-gray-500 block font-semibold">Lunch</span>
                    <span className="text-xs font-bold text-gray-900 font-mono">₹{Number(trip.costBreakdownFull.foodBreakdownINR.lunch || 0).toLocaleString()}</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-amber-200/60 text-center">
                    <span className="text-[10px] text-gray-500 block font-semibold">Dinner</span>
                    <span className="text-xs font-bold text-gray-900 font-mono">₹{Number(trip.costBreakdownFull.foodBreakdownINR.dinner || 0).toLocaleString()}</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-amber-200/60 text-center">
                    <span className="text-[10px] text-gray-500 block font-semibold">Snacks &amp; Drinks</span>
                    <span className="text-xs font-bold text-gray-900 font-mono">₹{Number(trip.costBreakdownFull.foodBreakdownINR.snacksAndDrinks || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Hidden Costs, Taxes & Permits Breakdown */}
            {trip.costBreakdownFull?.hiddenCostsINR && typeof trip.costBreakdownFull.hiddenCostsINR === 'object' && (
              <div className="p-4 rounded-2xl bg-purple-50/40 border border-purple-100 space-y-2">
                <span className="text-[11px] uppercase tracking-wider font-bold text-purple-900 block">
                  Hidden Costs, Taxes &amp; Permits
                </span>
                <div className="grid grid-cols-3 gap-2">
                  <div className="p-2 rounded-xl bg-white border border-purple-200/60 text-center">
                    <span className="text-[10px] text-gray-500 block font-semibold">Tolls &amp; Taxes</span>
                    <span className="text-xs font-bold text-gray-900 font-mono">₹{Number(trip.costBreakdownFull.hiddenCostsINR.tollsAndTaxes || 0).toLocaleString()}</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-purple-200/60 text-center">
                    <span className="text-[10px] text-gray-500 block font-semibold">Tips &amp; Gratuities</span>
                    <span className="text-xs font-bold text-gray-900 font-mono">₹{Number(trip.costBreakdownFull.hiddenCostsINR.tips || 0).toLocaleString()}</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-purple-200/60 text-center">
                    <span className="text-[10px] text-gray-500 block font-semibold">Permits</span>
                    <span className="text-xs font-bold text-gray-900 font-mono">₹{Number(trip.costBreakdownFull.hiddenCostsINR.permits || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Range Breakdown Cards */}
            {(costSummary.minimumCostINR || costSummary.maxCostINR || costSummary.perPersonINR) && (
              <div className="grid grid-cols-3 gap-2.5 pt-2">
                {costSummary.minimumCostINR && (
                  <div className="p-3 bg-teal-50/60 rounded-xl border border-teal-100 text-center">
                    <span className="text-[10px] font-bold text-teal-700 uppercase block">Min Budget</span>
                    <span className="text-xs font-black text-gray-900">₹{costSummary.minimumCostINR.toLocaleString()}</span>
                  </div>
                )}
                {costSummary.safeCostINR && (
                  <div className="p-3 bg-emerald-50/60 rounded-xl border border-emerald-100 text-center">
                    <span className="text-[10px] font-bold text-emerald-700 uppercase block">Recommended</span>
                    <span className="text-xs font-black text-emerald-800">₹{costSummary.safeCostINR.toLocaleString()}</span>
                  </div>
                )}
                {costSummary.perPersonINR && (
                  <div className="p-3 bg-sky-50/60 rounded-xl border border-sky-100 text-center">
                    <span className="text-[10px] font-bold text-sky-700 uppercase block">Per Traveler</span>
                    <span className="text-xs font-black text-sky-900">₹{costSummary.perPersonINR.toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Travel Insights, Logistics & Survival Guide ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Travel Intelligence & Insights */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm space-y-5">
          <div className="flex items-center gap-2.5 border-b border-gray-100 pb-3">
            <Sparkles className="w-5 h-5 text-[#2A9D8F]" />
            <h3 className="text-lg font-bold text-gray-900">Curated Travel Intelligence</h3>
          </div>

          <div className="space-y-4">
            {travelInsights.bestExperiences && travelInsights.bestExperiences.length > 0 && (
              <div className="space-y-2">
                <span className="text-xs font-bold text-teal-700 uppercase tracking-wider block">
                  Top Recommended Experiences
                </span>
                <div className="space-y-1.5">
                  {travelInsights.bestExperiences.map((exp, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
                      <CheckCircle2 className="w-3.5 h-3.5 text-teal-600 shrink-0 mt-0.5" />
                      <span>{exp}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {travelInsights.hiddenGems && travelInsights.hiddenGems.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <span className="text-xs font-bold text-amber-600 uppercase tracking-wider block">
                  Hidden Gems &amp; Offbeat Spots
                </span>
                <div className="space-y-1.5">
                  {travelInsights.hiddenGems.map((gem, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
                      <Sparkles className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                      <span>{gem}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {travelInsights.cautionPoints && travelInsights.cautionPoints.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <span className="text-xs font-bold text-rose-600 uppercase tracking-wider block">
                  Important Caution Points
                </span>
                <div className="space-y-1.5">
                  {travelInsights.cautionPoints.map((pt, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
                      <AlertTriangle className="w-3.5 h-3.5 text-rose-500 shrink-0 mt-0.5" />
                      <span>{pt}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {travelInsights.bestTimeToVisit && (
              <div className="space-y-1.5 pt-2 border-t border-gray-100">
                <span className="text-xs font-bold text-teal-800 uppercase tracking-wider block">
                  Best Time to Visit
                </span>
                <p className="text-xs text-gray-700 bg-teal-50/50 p-2.5 rounded-xl border border-teal-100">
                  {travelInsights.bestTimeToVisit}
                </p>
              </div>
            )}

            {travelInsights.sustainabilityTips && travelInsights.sustainabilityTips.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider block">
                  Sustainability &amp; Cultural Etiquette
                </span>
                <div className="space-y-1.5">
                  {travelInsights.sustainabilityTips.map((tip, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Logistics & Safety Guide */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm space-y-5">
          <div className="flex items-center gap-2.5 border-b border-gray-100 pb-3">
            <ShieldCheck className="w-5 h-5 text-[#2A9D8F]" />
            <h3 className="text-lg font-bold text-gray-900">Logistics &amp; Safety Guide</h3>
          </div>

          <div className="space-y-4">
            {logistics.packingList && logistics.packingList.length > 0 && (
              <div className="space-y-2">
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                  <ShoppingBag className="w-3.5 h-3.5 text-[#2A9D8F]" />
                  <span>Essential Packing Checklist</span>
                </span>
                <div className="flex flex-wrap gap-2">
                  {logistics.packingList.map((item, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium text-gray-700"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {logistics.healthAndSafety && logistics.healthAndSafety.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                  <HeartPulse className="w-3.5 h-3.5 text-rose-500" />
                  <span>Health &amp; Altitude Protocols</span>
                </span>
                <div className="space-y-1.5">
                  {logistics.healthAndSafety.map((rec, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
                      <CheckCircle2 className="w-3.5 h-3.5 text-rose-500 shrink-0 mt-0.5" />
                      <span>{rec}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {survivalGuide.scamWarnings && survivalGuide.scamWarnings.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <span className="text-xs font-bold text-amber-700 uppercase tracking-wider flex items-center gap-1.5">
                  <ShieldAlert className="w-3.5 h-3.5 text-amber-500" />
                  <span>Local Scam Advisories</span>
                </span>
                <div className="space-y-1.5">
                  {survivalGuide.scamWarnings.map((scam, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
                      <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                      <span>{scam}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {survivalGuide.localAppsToDownload && survivalGuide.localAppsToDownload.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <span className="text-xs font-bold text-blue-700 uppercase tracking-wider block">
                  Recommended Local Apps
                </span>
                <div className="flex flex-wrap gap-2">
                  {survivalGuide.localAppsToDownload.map((app, i) => (
                    <span key={i} className="px-3 py-1 bg-blue-50 border border-blue-200 rounded-xl text-xs font-medium text-blue-800">
                      {app}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {survivalGuide.emergencyContacts && survivalGuide.emergencyContacts.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <span className="text-xs font-bold text-rose-700 uppercase tracking-wider block">
                  Emergency Contacts &amp; Helplines
                </span>
                <div className="space-y-1.5">
                  {survivalGuide.emergencyContacts.map((contact, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-rose-900 bg-rose-50/60 p-2 rounded-xl border border-rose-100">
                      <PhoneCall className="w-3.5 h-3.5 text-rose-600 shrink-0 mt-0.5" />
                      <span>{contact}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {survivalGuide.culturalNorms && survivalGuide.culturalNorms.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <span className="text-xs font-bold text-purple-700 uppercase tracking-wider block">
                  Cultural Norms &amp; Local Etiquette
                </span>
                <div className="space-y-1.5">
                  {survivalGuide.culturalNorms.map((norm, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
                      <CheckCircle2 className="w-3.5 h-3.5 text-purple-500 shrink-0 mt-0.5" />
                      <span>{norm}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Social Share Modal (Instagram Post Preview) ── */}
      <SocialShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        trip={trip}
        organizer={organizerUser}
      />

      {/* ── Companion Propose Changes (GitHub PR Workflow) Modal ── */}
      {showProposeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl max-w-xl w-full p-6 sm:p-8 space-y-5 shadow-2xl border border-gray-100">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 pb-3">
              <div>
                <span className="text-xs font-black text-teal-600 uppercase tracking-wider flex items-center gap-1.5">
                  <GitPullRequest className="w-3.5 h-3.5" />
                  <span>Propose Itinerary Changes (PR)</span>
                </span>
                <h3 className="text-lg font-black text-gray-900 mt-0.5">
                  Suggest Updates to Trip Organizer
                </h3>
              </div>
              <button
                onClick={() => setShowProposeModal(false)}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitProposal} className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-gray-700 block mb-1">
                  Change Request Title <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="E.g., Adjusted travel dates and pacing"
                  value={proposeDraft.title || ''}
                  onChange={(e) => setProposeDraft({ ...proposeDraft, title: e.target.value })}
                  className="w-full px-3.5 py-2.5 border rounded-xl focus:ring-2 focus:ring-[#2A9D8F] outline-none text-gray-800"
                />
              </div>

              <div>
                <label className="font-bold text-gray-700 block mb-1">
                  Message / Reason for Organizer <span className="text-rose-500">*</span>
                </label>
                <textarea
                  rows={3}
                  required
                  placeholder="E.g., I'd like to adjust our travel dates to accommodate work schedules, and keep daily walking pacing moderate for elderly family members."
                  value={proposeDraft.note}
                  onChange={(e) => setProposeDraft({ ...proposeDraft, note: e.target.value })}
                  className="w-full px-3.5 py-2.5 border rounded-xl focus:ring-2 focus:ring-[#2A9D8F] outline-none text-gray-800"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-gray-700 block mb-1">Proposed Start Date</label>
                  <input
                    type="date"
                    value={proposeDraft.startDate}
                    onChange={(e) => setProposeDraft({ ...proposeDraft, startDate: e.target.value })}
                    className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-[#2A9D8F] outline-none text-gray-800"
                  />
                </div>
                <div>
                  <label className="font-bold text-gray-700 block mb-1">Proposed End Date</label>
                  <input
                    type="date"
                    value={proposeDraft.endDate}
                    onChange={(e) => setProposeDraft({ ...proposeDraft, endDate: e.target.value })}
                    className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-[#2A9D8F] outline-none text-gray-800"
                  />
                </div>
              </div>

              <div>
                <label className="font-bold text-gray-700 block mb-1">Proposed Total Budget (₹)</label>
                <input
                  type="number"
                  placeholder="50000"
                  value={proposeDraft.budget}
                  onChange={(e) => setProposeDraft({ ...proposeDraft, budget: e.target.value })}
                  className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-[#2A9D8F] outline-none font-mono text-gray-800"
                />
              </div>

              <div>
                <label className="font-bold text-gray-700 block mb-1">
                  Dietary Preferences / Mobility Notes
                </label>
                <input
                  type="text"
                  placeholder="E.g., Pure Vegetarian / Jain meals required; preference for afternoon rest"
                  value={proposeDraft.travelerRequirements}
                  onChange={(e) => setProposeDraft({ ...proposeDraft, travelerRequirements: e.target.value })}
                  className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-[#2A9D8F] outline-none text-gray-800"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowProposeModal(false)}
                  className="px-4 py-2 text-xs font-bold text-gray-600 hover:bg-gray-100 rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingProposal}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white rounded-xl text-xs font-black shadow cursor-pointer disabled:opacity-50"
                >
                  {isSubmittingProposal ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  <span>{isSubmittingProposal ? 'Submitting...' : 'Submit Pull Request'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

